import {
  BreakOption,
  CompanyBasicInfo,
  CompanyDetailAndJobGroups,
  CompanyEmploymentTypes,
  CompanyFormPayload,
} from '@types';
import { CompanyTheme } from 'shared/contexts/theme-provider';
import {
  ChangeCompanySettingsPayload,
  CompanyInfoByCocNumber,
  CreateLimitedCompanyPayload,
  CreateLimitedCompanyResponse,
  FlexWorkerForNotWelcomeList,
  GetBreakOptionsResponse,
  GetFlexWorkerForNotWelcomeListResponse,
  GetNotWelcomeListResponse,
  UpdateCompanyThemePayload,
} from './company-service.types';
import { OgpApiService } from './ogp-api-service';

export class CompanyService {
  constructor(private readonly ogpApiService: OgpApiService) {}

  // Get only basic company information
  public getCompanyBasicInfo = async () => {
    const companyBasicInfo = await this.ogpApiService.get<CompanyBasicInfo>({
      path: `companies/basic-info/`,
    });
    return companyBasicInfo;
  };

  /**
   * Get a single company and its related jobGroups
   */
  public getCompanyDetailAndJobGroups = async () => {
    const companyDetailAndJobGroups = await this.ogpApiService.get<CompanyDetailAndJobGroups>({
      path: `companies/`,
    });

    return companyDetailAndJobGroups;
  };

  public getCompanyEmploymentTypes = async () => {
    const companyEmploymentTypes = await this.ogpApiService.get<CompanyEmploymentTypes>({
      path: `companies/employment-types/`,
    });

    return companyEmploymentTypes;
  };

  /**
   * Get a single company
   */
  public getBreakOptions = async (): Promise<BreakOption[]> => {
    const response = await this.ogpApiService.get<GetBreakOptionsResponse>({
      path: `companies/break-options`,
    });

    return response.breakOptions;
  };

  public getCompanyInfoByCocNumber = async (cocNumber: string) => {
    return await this.ogpApiService.get<CompanyInfoByCocNumber>({
      path: `companies/coc/${cocNumber}`,
    });
  };

  public createLimitedCompany = async (payload: CreateLimitedCompanyPayload) => {
    return await this.ogpApiService.create<CreateLimitedCompanyResponse>({
      path: 'companies',
      body: payload,
    });
  };

  /**
   * Update an existing company
   */
  public updateCompany = async (company: CompanyFormPayload) => {
    return await this.ogpApiService.update({
      path: `companies/company`,
      body: company,
    });
  };

  /**
   * Update company settings
   */
  public updateCompanySettings = async (payload: ChangeCompanySettingsPayload) => {
    return await this.ogpApiService.update({
      path: `companies/company-settings`,
      body: payload,
    });
  };

  public getCompanyTheme = async (): Promise<CompanyTheme | null> => {
    return await this.ogpApiService.get<CompanyTheme | null>({
      path: `companies/theme`,
    });
  };

  /**
   * Update company theme. When payload.logo is present, sends multipart with logos; otherwise sends JSON with colors only.
   */
  public updateCompanyTheme = async (payload: UpdateCompanyThemePayload) => {
    const hasLogoFiles =
      !!payload.logo && (!!payload.logo.portalLogoWide || !!payload.logo.portalLogoSquare);
    const body = hasLogoFiles ? getThemePayloadFormData(payload) : { colors: payload.colors };

    return await this.ogpApiService.update({
      path: `companies/theme`,
      body,
    });
  };

  /**
   * Returns a list of flexworkers that are on the 'not welcome list' for a company
   */
  public getNotWelcomeList = async () => {
    const response = await this.ogpApiService.get({ path: 'not-welcome-list' });
    const result = GetNotWelcomeListResponse.parse(response);
    return result;
  };

  /**
   * Returns a list of flexworkers can be added on the 'not welcome list' for a company
   */
  public getAvailableFlexWorkers = async (filter: string, signal: AbortSignal | undefined) => {
    const response = await this.ogpApiService.get<FlexWorkerForNotWelcomeList[]>({
      path: 'not-welcome-list/available',
      params: { filter },
      signal,
    });

    const result = GetFlexWorkerForNotWelcomeListResponse.parse(response);
    return result;
  };

  /**
   * Returns a list of flexworkers that are on the 'not welcome list' for a company
   */
  public addFlexWorkerToNotWelcomeList = async (
    flexWorkerId: string,
    reason: string,
    departmentIds: string[],
    shouldCancelAll: boolean
  ) => {
    return await this.ogpApiService.create<undefined>({
      path: `not-welcome-list/add/${flexWorkerId}`,
      body: { reason, departmentIds, shouldCancelAll },
    });
  };

  /**
   * Returns a list of flexworkers can be added on the 'not welcome list' for a company
   */
  public removeFlexWorkerFromNotWelcomeList = async (flexWorkerId: string) => {
    return await this.ogpApiService.delete<undefined>({
      path: `not-welcome-list/remove/${flexWorkerId}`,
    });
  };
}

function getThemePayloadFormData(payload: UpdateCompanyThemePayload): FormData {
  const formData = new FormData();
  formData.append('colors[primaryColor]', payload.colors.primaryColor);
  formData.append('colors[secondaryColor]', payload.colors.secondaryColor);
  formData.append('colors[primaryAccent]', payload.colors.primaryAccent);
  formData.append('colors[secondaryAccent]', payload.colors.secondaryAccent);
  if (payload.logo?.portalLogoWide) {
    formData.append('logo.portalLogoWide', payload.logo.portalLogoWide);
  }
  if (payload.logo?.portalLogoSquare) {
    formData.append('logo.portalLogoSquare', payload.logo.portalLogoSquare);
  }
  return formData;
}
